import { useGetSettingsQuery } from "@/store/api";

/**
 * EngineStateBanner — at the top of an engine's page, show a banner
 * whenever the engine is OFF / PAUSED in runtime settings. Without it,
 * the user sees historical data and assumes the engine is broken.
 *
 * Example:
 *   <EngineStateBanner enabledKey="breakout_engine_enabled"
 *                       label="Breakout engine"
 *                       hostHint="auto-controlled by the engine governor in hostile regime" />
 */
export function EngineStateBanner({
  enabledKey,
  pausedKey,
  label,
  hostHint,
}: {
  enabledKey?: string;
  pausedKey?: string;
  label: string;
  hostHint?: string;
}) {
  const settings = useGetSettingsQuery();
  if (settings.isLoading || !settings.data) return null;
  const enabled = enabledKey
    ? (settings.data as any)[enabledKey] === true
    : true;
  const paused = pausedKey
    ? (settings.data as any)[pausedKey] === true
    : false;
  if (enabled && !paused) return null;   // engine is live, no banner

  const state = !enabled ? "DISABLED" : "PAUSED";
  return (
    <div className="border-l-4 border-l-yellow-500 bg-bg2 rounded p-3 text-sm space-y-1">
      <div className="font-semibold text-yellow-500">
        ⏸ {label} is {state}
      </div>
      <div className="text-xs text-muted">
        Data below is historical. New rows will not appear until the engine
        is re-armed.
        {hostHint && <> {hostHint}</>}
      </div>
    </div>
  );
}
